/**
 * ProtectedLink - The Intelligent Standard
 *
 * Link component that shows login modal for unauthenticated users
 * before navigating to protected routes.
 */

import {
  MouseEvent,
  startTransition,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from './LoginModal';

interface ProtectedLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function ProtectedLink({
  to,
  children,
  className,
  style,
}: ProtectedLinkProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  // Use ref for pending navigation to avoid re-renders
  const pendingNavigationRef = useRef<string | null>(null);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (user) {
      navigate(to);
    } else {
      e.currentTarget.blur();
      pendingNavigationRef.current = to;
      setShowModal(true);
    }
  };

  // Watch for authentication while modal is open
  // This effect reacts to external auth state changes - a legitimate use case
  // where we need to close modal and navigate after successful login
  useEffect(() => {
    if (showModal && user && pendingNavigationRef.current) {
      const destination = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      // Use startTransition to batch state update and navigation together
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Legitimate: reacting to auth state change
      startTransition(() => {
        setShowModal(false);
        navigate(destination);
      });
    }
  }, [user, showModal, navigate]);

  return (
    <>
      <a href={to} onClick={handleClick} className={className} style={style}>
        {children}
      </a>

      {showModal && (
        <LoginModal
          onClose={() => {
            pendingNavigationRef.current = null;
            setShowModal(false);
          }}
        />
      )}
    </>
  );
}
